// Generated by DesignToCode

import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  input,
  output,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'app-regular-button',
  standalone: true,
  imports: [CommonModule, SvgIconComponent],
  templateUrl: './regular-button.html',
})
export class RegularButton {
  @ViewChild('button') public buttonRegularRef: ElementRef<HTMLElement> | null =
    null;

  public label = input.required<string>();
  public size = input<'small' | 'medium' | 'large'>('medium');
  public type = input<'primary' | 'secondary' | 'danger'>('primary');
  public iconLeft = input<string>();
  public iconRight = input<string>();
  public disabled = input<boolean>(false);
  public fullWidth = input<boolean>(false);
  public buttonClicked = output<boolean>();

  // Computed classes for the button
  public buttonClasses = computed(() => {
    const classes: string[] = [
      'group',
      'inline-flex',
      'items-center',
      'justify-center',
      'font-body',
      'font-semibold',
      'transition-colors',
      'focus:outline-none',
    ];

    // Size-based classes
    if (this.size() === 'large') {
      classes.push('text-md', 'leading-xl', 'px-sm', 'py-sm', 'rounded-xs');
    } else if (this.size() === 'medium') {
      classes.push('text-md', 'leading-xl', 'px-sm', 'py-xs', 'rounded-xs');
    } else if (this.size() === 'small') {
      classes.push('text-sm', 'leading-lg', 'px-2xs', 'py-3xs', 'rounded-2xs');
    }

    // Width classes
    if (this.fullWidth()) {
      classes.push('w-full');
    }

    // Gap between elements
    if (this.size() === 'small') {
      classes.push('gap-2xs');
    } else {
      classes.push('gap-xs');
    }

    // Type and state classes
    if (!this.disabled()) {
      // Primary button
      if (this.type() === 'primary') {
        classes.push('bg-navy-blue-90', 'text-white');
        classes.push('hover:bg-navy-blue-100');
        classes.push('active:bg-navy-blue-120');
        classes.push(
          'focus:bg-navy-blue-90',
          'focus:shadow-[0_0_0_3px_rgba(208,221,255,1)]',
        );
      }
      // Secondary button
      else if (this.type() === 'secondary') {
        classes.push('bg-white', 'text-grey-110', 'border', 'border-grey-100');
        classes.push('hover:shadow-[0_0_0_3px_rgba(208,221,255,1)]');
        classes.push('active:border-grey-120', 'active:text-grey-120');
        classes.push(
          'focus:border-grey-120',
          'focus:text-grey-120',
          'focus:shadow-[0_0_0_3px_rgba(208,221,255,1)]',
        );
      }
      // Danger button
      else if (this.type() === 'danger') {
        classes.push('bg-red-90', 'text-white');
        classes.push('hover:bg-red-110');
        classes.push('active:bg-red-120');
        classes.push('focus:shadow-[0_0_0_3px_rgba(254,189,186,1)]');
      }
    } else {
      // Disabled states
      if (this.type() === 'primary' || this.type() === 'danger') {
        classes.push(
          'bg-grey-70',
          'text-white',
          'border',
          'border-transparent',
          'cursor-not-allowed',
        );
      } else if (this.type() === 'secondary') {
        classes.push(
          'bg-white',
          'text-grey-80',
          'border',
          'border-grey-80',
          'cursor-not-allowed',
        );
      }
    }

    return classes.join(' ');
  });

  // Computed classes for the icons
  public iconClasses = computed(() => {
    const classes: string[] = [];

    if (this.size() === 'small') {
      classes.push('w-4', 'h-4');
    } else {
      classes.push('w-5', 'h-5');
    }

    if (this.type() === 'primary' || this.type() === 'danger') {
      classes.push('fill-white');
    } else if (this.type() === 'secondary') {
      if (this.disabled()) {
        classes.push('fill-grey-80');
      } else {
        classes.push(
          'fill-grey-110',
          'group-active:fill-grey-120',
          'group-focus:fill-grey-120',
        );
      }
    }

    return classes.join(' ');
  });

  /**
   * Emits the click event
   */
  public submit(): void {
    if (!this.disabled()) {
      this.buttonRegularRef?.nativeElement.blur();
      this.buttonClicked.emit(true);
    }
  }
}
